import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import { useUserEnvironmentActions } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { AuthApiTmpStorage } from '#/modules/auth/api/tmpStorage/types/AuthApiTmpStorage';
import { cmsApiSubscribeToSubsite } from '#/modules/cms/api/cmsApiSubscribeToSubsite';
import { useOrgSavedToast } from '#/modules/listing/orgs/hooks/useOrgSavedToast';
import { userDashboardApiCreateSavedListing } from '#/modules/userDashboard/api/savedListing/userDashboardApiCreateSavedListing';
import { useSavedSearchesActions } from '#/modules/userDashboard/contexts/SavedSearchesContext';
import { getRelativeURL } from '#/utils/url/getRelativeURL';
import { isExternalUrl } from '#/utils/url/isExternalUrl';
import { flash } from '#/zustand-stores/flashesStore';
import { showModal } from '#/zustand-stores/modalStore';

export function useAuthProcessTmpStorage() {
  const { i18n } = useI18n();
  const { showOrgSavedToast } = useOrgSavedToast();
  const { markActionsWhileLoggedOutProcessed } = useUserEnvironmentActions();
  const { createSavedSearch } = useSavedSearchesActions();
  const navigate = useNavigate();

  const authProcessTmpStorage = useCallback(
    async ({
      tmpStorage,
      replaceOnNavigate,
    }: {
      tmpStorage: AuthApiTmpStorage;
      replaceOnNavigate: boolean;
    }) => {
      // These are processed elsewhere
      // Ideally we should remove these types from "auth tmp storage" entirely,
      // making it more obvious that they belong elsewhere
      if (tmpStorage.type === 'cmsSubscribe') return false;

      switch (tmpStorage.type) {
        case 'redirect':
          if (isExternalUrl(tmpStorage.redirectUrl)) {
            window.location.href = tmpStorage.redirectUrl;
          } else {
            await navigate(getRelativeURL(tmpStorage.redirectUrl), {
              replace: replaceOnNavigate,
            });
          }
          break;
        case 'savedListing':
          await userDashboardApiCreateSavedListing(
            tmpStorage.savedListing.id,
            tmpStorage.savedListing.type,
          );

          if (tmpStorage.savedListing.type === 'ORG') showOrgSavedToast();
          break;
        case 'subscribedSubsite':
          await cmsApiSubscribeToSubsite(tmpStorage.subscribedSubsiteId);
          flash(i18n('Subscribed!'));
          showModal('SUBSCRIBED_TO_SUBSITE', {});
          break;
        case 'savedSearch':
          await createSavedSearch(tmpStorage.savedSearch);
          break;
      }

      markActionsWhileLoggedOutProcessed();
      return true;
    },
    [
      markActionsWhileLoggedOutProcessed,
      showOrgSavedToast,
      navigate,
      createSavedSearch,
      i18n,
    ],
  );

  return { authProcessTmpStorage };
}
